/**
 * Events API client — typed, envelope-aware fetchers for the
 * `/v1/contract/:module` family of endpoints.
 *
 * Each helper returns the full Envelope (not the unwrapped data) so the
 * caller can access `meta.anomalies`, `source_pulled_at`, and
 * `recorded` denominators alongside `data`.
 */
import { z } from 'zod';
import {
  Envelope,
  zAnomaly,
  zChannels,
  zDistrict,
  zOrganizerFormatMix,
  zRecorded,
  zSpeakerByFormat,
  zSpeakers,
  zTotals,
  zTopSpeakers,
} from '@biyahad/contracts';
import type {
  Channel,
  Channels,
  District,
  Envelope as EnvelopeT,
  Organizer,
  SpeakerByFormat,
  SpeakerCell,
  SpeakerFormatRow,
  Speakers,
  TopSpeaker,
  TopSpeakers,
  Totals,
} from '@biyahad/contracts';
import { getConfig } from '../../config/env';
import { getAuthToken } from '../runtime';

/**
 * Build the request init for an events-API call. Centralised so every
 * fetcher in this file picks up the Authorization header from the cached
 * Cognito access token (set by AuthProvider on every render).
 */
function authedInit(opts?: { signal?: AbortSignal }): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return { headers, signal: opts?.signal };
}

/* ------------------------------------------------------------------ */
/* Funnel module payload                                              */
/* ------------------------------------------------------------------ */

/**
 * Per-district funnel row (mirrors window.DISTRICTS funnel slice).
 *
 * The spec lists `{ district, reached, rsvp_yes, attended, walk_in }`.
 * Real API rows currently use `id` / `he` instead of `district`; we
 * accept either so the schema does not reject either shape.
 */
export const zFunnelDistrict = z
  .object({
    district: z.string().optional(),
    id: z.string().optional(),
    he: z.string().optional(),
    reached: z.number().nonnegative(),
    rsvp_yes: z.number().nonnegative(),
    attended: z.number().nonnegative(),
    walk_in: z.number().nonnegative(),
  })
  .passthrough();
export type FunnelDistrict = z.infer<typeof zFunnelDistrict>;

/**
 * Funnel module payload. The documented shape is
 *   { totals: Totals, by_district?: FunnelDistrict[] }
 * but during the API transition the endpoint may instead return the
 * `Totals` shape directly (the response from /v1/contract/totals). The
 * fetcher normalises to the documented shape before returning.
 */
export const zFunnelData = z.object({
  totals: zTotals,
  by_district: z.array(zFunnelDistrict).optional(),
});
export type FunnelData = z.infer<typeof zFunnelData>;

export const zFunnelEnvelope = Envelope(zFunnelData);
export type FunnelEnvelope = EnvelopeT<FunnelData>;

/* ------------------------------------------------------------------ */
/* Organizers module payload                                          */
/* ------------------------------------------------------------------ */

/**
 * Organizer row as returned by `/v1/contract/organizers`.
 *
 * Mirrors the contracts-package `zOrganizer` but is intentionally
 * permissive (`.passthrough()`, optional `district`/`format_mix`) so a
 * mid-deploy backend can ship slightly looser rows without breaking
 * the view. Per the brief, live data is currently returning Airtable
 * record IDs (e.g. `rechkuSE623b8rOz0`) in the `he` field — that gets
 * detected in the view layer, not enforced here.
 *
 * `yes_show` is intentionally NOT a field: it is derived in the UI as
 * `(attended - walk_in) / rsvp_yes`.
 */
export const zOrganizerRow = z
  .object({
    he: z.string(),
    district: z.string().optional().default(''),
    events: z.number().nonnegative(),
    rsvp_yes: z.number().nonnegative(),
    attended: z.number().nonnegative(),
    walk_in: z.number().nonnegative(),
    format_mix: zOrganizerFormatMix.optional(),
    insufficient_data: z.boolean().optional(),
  })
  .passthrough();
export type OrganizerRow = z.infer<typeof zOrganizerRow>;

export const zOrganizersData = z.array(zOrganizerRow);
export type OrganizersData = z.infer<typeof zOrganizersData>;

export const zOrganizersEnvelope = Envelope(zOrganizersData);
export type OrganizersEnvelope = EnvelopeT<OrganizersData>;

/* ------------------------------------------------------------------ */
/* Coverage module payload                                            */
/* ------------------------------------------------------------------ */

/**
 * Coverage module payload — one row per district plus campaign totals.
 *
 * Each district row carries deterministic tile-cartogram coordinates
 * (`col`/`row`), the funnel metrics needed for density encoding, the
 * recorded-attendance denominator, and a directional trend label.
 *
 * Schema mirrors the canonical `zDistrict` in @biyahad/contracts, but
 * lives behind `.passthrough()` here so a backend hot-fix that adds
 * extra fields will not break the view.
 */
export const zCoverageDistrict = zDistrict.passthrough();
export type CoverageDistrict = District;

export const zCoverageData = z.object({
  districts: z.array(zCoverageDistrict),
  totals: zTotals,
});
export type CoverageData = z.infer<typeof zCoverageData>;

export const zCoverageEnvelope = Envelope(zCoverageData);
export type CoverageEnvelope = EnvelopeT<CoverageData>;

/* ------------------------------------------------------------------ */
/* Formats module payload                                             */
/* ------------------------------------------------------------------ */

/**
 * One format row from `/v1/contract/formats`. `id` is the internal
 * English handle (`rally`, `parlor`, `town-hall`, `virtual`); `he` is
 * the Hebrew display name (`כנס`, `חוג בית`, …). `yes_show` is NOT a
 * field — derive it in the view as `(attended - walk_in) / rsvp_yes`.
 */
export const zFormatRow = z
  .object({
    id: z.string(),
    he: z.string(),
    events: z.number().nonnegative(),
    reached: z.number().nonnegative(),
    rsvp_yes: z.number().nonnegative(),
    attended: z.number().nonnegative(),
    walk_in: z.number().nonnegative(),
    avg_per_event: z.number().nonnegative(),
  })
  .passthrough();
export type FormatRow = z.infer<typeof zFormatRow>;

export const zFormatsData = z.array(zFormatRow);
export type FormatsData = z.infer<typeof zFormatsData>;

export const zFormatsEnvelope = Envelope(zFormatsData);
export type FormatsEnvelope = EnvelopeT<FormatsData>;

/* ------------------------------------------------------------------ */
/* Channels module payload                                            */
/* ------------------------------------------------------------------ */

/**
 * Channels module payload — array of `Channel` rows from the
 * `/v1/contract/channels` endpoint. Mirrors the contracts-package
 * `zChannels` exactly: each row has `{ id, he, total_signups,
 * rate_overall, anomaly?, note? }`. The view derives `attended` as
 * `round(total_signups * rate_overall)`.
 */
export const zChannelsData = zChannels;
export type ChannelsData = Channels;
export type ChannelRow = Channel;

export const zChannelsEnvelope = Envelope(zChannelsData);
export type ChannelsEnvelope = EnvelopeT<ChannelsData>;

/* ------------------------------------------------------------------ */
/* Speakers module payload                                            */
/* ------------------------------------------------------------------ */

/**
 * Speakers (A2) module payload — `{ matrix, top_speakers }` from
 * `/v1/contract/speakers`.
 *
 * - `matrix` keys: outer = speaker class
 *   (`principal | senior | guest | local-only`); inner = format id
 *   (`rally | parlor | town-hall | virtual`). Each cell =
 *   `{ events, attended_avg, yes_show, n }`. Sparse cells (events < 3)
 *   carry `null` for `attended_avg`/`yes_show` — the view renders those
 *   as "—" so we never show a fabricated rate.
 *
 * - `top_speakers` is the per-named-MK leaderboard powered by the three
 *   structured event → MK link fields on the events table. Each row
 *   carries `{ mk_id, name, party, events, attended, yes_show, format_mix }`,
 *   sorted by `events` desc.
 */
export const zSpeakersData = zSpeakers;
export type SpeakersData = Speakers;

export const zSpeakersEnvelope = Envelope(zSpeakersData);
export type SpeakersEnvelope = EnvelopeT<SpeakersData>;

/* ------------------------------------------------------------------ */
/* Trends bundle — three slices pulled from a single contract call    */
/* ------------------------------------------------------------------ */

/**
 * `trends` is a map of district-name → weekly attendance counts.
 * Delivered as a plain object, e.g.
 *   { "תל אביב": [0, 0, …, 1315], "נגב": [...], ... }
 *
 * Keys are the Hebrew district names (matching the keys returned by
 * /v1/contract/districts).
 */
export const zTrendsMap = z.record(z.array(z.number()));
export type TrendsMap = z.infer<typeof zTrendsMap>;

/** National weekly aggregate, aligned to `week_labels` by index. */
export const zCampaignTrend = z.array(z.number());
export type CampaignTrend = z.infer<typeof zCampaignTrend>;

/** X-axis labels (e.g. "שב׳ 1", "2", …, "היום"). */
export const zWeekLabels = z.array(z.string());
export type WeekLabels = z.infer<typeof zWeekLabels>;

/**
 * Per-district trend classifier — mirrors the districts row but only
 * needs `id`/`he` + the `trend` tag for the secondary panel.
 */
export const zTrendDistrict = z
  .object({
    id: z.string().optional(),
    he: z.string().optional(),
    trend: z.enum(['accelerating', 'stable', 'stalling']).optional(),
  })
  .passthrough();
export type TrendDistrict = z.infer<typeof zTrendDistrict>;

/**
 * View-shaped bundle the Trends view consumes. Resolved from a single
 * `/v1/contract` round-trip so the three slices share an envelope
 * (freshness, anomalies, recorded denominators).
 */
export interface TrendsBundle {
  generated_at: string;
  source_pulled_at: string;
  scope: string;
  recorded: EnvelopeT<unknown>['recorded'];
  meta: EnvelopeT<unknown>['meta'];
  trends: TrendsMap;
  campaign_trend: CampaignTrend;
  week_labels: WeekLabels;
  districts: TrendDistrict[];
}

/* ------------------------------------------------------------------ */
/* Loose envelope used for shape normalisation during API transition  */
/* ------------------------------------------------------------------ */

const zLooseEnvelope = z.object({
  generated_at: z.string(),
  source_pulled_at: z.string(),
  scope: z.string(),
  recorded: zRecorded,
  meta: z
    .object({
      mock: z.boolean().optional(),
      anomalies: z.array(zAnomaly).default([]),
    })
    .default({ anomalies: [] }),
  data: z.unknown(),
});

/* ------------------------------------------------------------------ */
/* Module fetcher                                                     */
/* ------------------------------------------------------------------ */

export interface ModuleFilters {
  scope?: string;
}

export interface FetchModuleOptions {
  signal?: AbortSignal;
}

function apiBase(): string {
  const cfg = getConfig();
  return cfg.API_BASE_URL.replace(/\/$/, '');
}

/**
 * Fetch a single contract module envelope. Throws on non-2xx or on
 * schema validation failure — both surface in react-query's `error`.
 */
export async function fetchModule(
  moduleId: 'funnel',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<FunnelEnvelope>;
export async function fetchModule(
  moduleId: 'organizers',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<OrganizersEnvelope>;
export async function fetchModule(
  moduleId: 'coverage',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<CoverageEnvelope>;
export async function fetchModule(
  moduleId: 'formats',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<FormatsEnvelope>;
export async function fetchModule(
  moduleId: 'channels',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<ChannelsEnvelope>;
export async function fetchModule(
  moduleId: 'speakers',
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<SpeakersEnvelope>;
export async function fetchModule(
  moduleId: string,
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<
  | FunnelEnvelope
  | OrganizersEnvelope
  | CoverageEnvelope
  | FormatsEnvelope
  | ChannelsEnvelope
  | SpeakersEnvelope
> {
  const base = apiBase();
  const scope = filters?.scope ?? 'national';
  const url = `${base}/v1/contract/${encodeURIComponent(moduleId)}?scope=${encodeURIComponent(scope)}`;

  const res = await fetch(url, authedInit(opts));
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `GET ${url} → ${res.status} ${res.statusText}${text ? ` — ${text}` : ''}`,
    );
  }

  const raw = (await res.json()) as unknown;
  // Validate the outer envelope shape first; this gives a clean error
  // when the API shape drifts so failures land here rather than in the
  // chart.
  const loose = zLooseEnvelope.parse(raw);

  if (moduleId === 'funnel') {
    const normalised = normaliseFunnelData(loose.data);
    const envelope: FunnelEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data: normalised,
    };
    // Full structural validation so any drift surfaces here.
    return zFunnelEnvelope.parse(envelope);
  }

  if (moduleId === 'organizers') {
    const rows = normaliseOrganizersData(loose.data);
    const envelope: OrganizersEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data: rows,
    };
    return zOrganizersEnvelope.parse(envelope);
  }

  if (moduleId === 'coverage') {
    const data = normaliseCoverageData(loose.data);
    const envelope: CoverageEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data,
    };
    return zCoverageEnvelope.parse(envelope);
  }

  if (moduleId === 'formats') {
    const rows = normaliseFormatsData(loose.data);
    const envelope: FormatsEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data: rows,
    };
    return zFormatsEnvelope.parse(envelope);
  }

  if (moduleId === 'channels') {
    const rows = normaliseChannelsData(loose.data);
    const envelope: ChannelsEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data: rows,
    };
    return zChannelsEnvelope.parse(envelope);
  }

  if (moduleId === 'speakers') {
    const matrix = normaliseSpeakersData(loose.data);
    const envelope: SpeakersEnvelope = {
      generated_at: loose.generated_at,
      source_pulled_at: loose.source_pulled_at,
      scope: loose.scope,
      recorded: loose.recorded,
      meta: loose.meta,
      data: matrix,
    };
    return zSpeakersEnvelope.parse(envelope);
  }

  // Other modules: caller should provide its own type.
  throw new Error(`fetchModule: unsupported moduleId "${moduleId}"`);
}

/**
 * The funnel endpoint may return either:
 *   - { totals: Totals, by_district?: [...] }  (documented shape)
 *   - Totals                                   (legacy /v1/contract/totals)
 *
 * Normalise both to the documented shape.
 */
function normaliseFunnelData(raw: unknown): FunnelData {
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    // Already in documented shape.
    if ('totals' in obj && obj.totals && typeof obj.totals === 'object') {
      const totals = zTotals.parse(obj.totals);
      const by_district = Array.isArray(obj.by_district)
        ? (z.array(zFunnelDistrict).parse(obj.by_district) as FunnelDistrict[])
        : undefined;
      return by_district !== undefined ? { totals, by_district } : { totals };
    }
    // Legacy: payload IS the Totals object.
    if ('reached' in obj && 'rsvp_yes' in obj && 'attended' in obj) {
      return { totals: zTotals.parse(obj) };
    }
  }
  throw new Error('fetchModule(funnel): unrecognised data shape');
}

/**
 * The organizers endpoint returns an array of rows, but a future
 * envelope variant might wrap them in `{ rows: [...] }`. Accept both
 * for resilience.
 */
function normaliseOrganizersData(raw: unknown): OrganizersData {
  if (Array.isArray(raw)) {
    return zOrganizersData.parse(raw);
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
    return zOrganizersData.parse((raw as { rows: unknown }).rows);
  }
  throw new Error('fetchModule(organizers): unrecognised data shape');
}

/**
 * The coverage endpoint returns `{ districts: District[], totals: Totals }`.
 * If the backend ever ships only the array form (`District[]`) we synthesise
 * an empty-ish totals block so the view does not explode — but the API has
 * always returned the documented shape, so this is just defence-in-depth.
 */
function normaliseCoverageData(raw: unknown): CoverageData {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.districts) && obj.totals && typeof obj.totals === 'object') {
      return zCoverageData.parse({
        districts: obj.districts,
        totals: obj.totals,
      });
    }
  }
  throw new Error('fetchModule(coverage): unrecognised data shape');
}

/**
 * The formats endpoint returns an array of `FormatRow`. Accept the
 * `{ rows: [...] }` envelope variant defensively, mirroring the
 * organizers normaliser.
 */
function normaliseFormatsData(raw: unknown): FormatsData {
  if (Array.isArray(raw)) {
    return zFormatsData.parse(raw);
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
    return zFormatsData.parse((raw as { rows: unknown }).rows);
  }
  throw new Error('fetchModule(formats): unrecognised data shape');
}

/**
 * The channels endpoint returns an array of `Channel` rows. Accept the
 * `{ rows: [...] }` envelope variant defensively, matching the
 * organizers/formats normalisers.
 */
function normaliseChannelsData(raw: unknown): ChannelsData {
  if (Array.isArray(raw)) {
    return zChannelsData.parse(raw);
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { rows?: unknown }).rows)) {
    return zChannelsData.parse((raw as { rows: unknown }).rows);
  }
  throw new Error('fetchModule(channels): unrecognised data shape');
}

/**
 * The speakers endpoint now returns `{ matrix, top_speakers }` (the
 * combined slice). Legacy responses returned the matrix object directly
 * — when we detect that shape we synthesise `top_speakers: []` so the
 * view degrades gracefully during an API hot-deploy.
 */
function normaliseSpeakersData(raw: unknown): SpeakersData {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;

    // New shape: `{ matrix, top_speakers }`.
    if ('matrix' in obj && obj.matrix && typeof obj.matrix === 'object') {
      const matrix = zSpeakerByFormat.parse(obj.matrix);
      const top_speakers = Array.isArray(obj.top_speakers)
        ? zTopSpeakers.parse(obj.top_speakers)
        : [];
      return { matrix, top_speakers };
    }

    // Legacy shape: matrix at the top level.
    const looksLikeMatrix =
      'principal' in obj || 'senior' in obj || 'guest' in obj || 'local-only' in obj;
    if (looksLikeMatrix) {
      return {
        matrix: zSpeakerByFormat.parse(obj),
        top_speakers: [],
      };
    }
  }
  throw new Error('fetchModule(speakers): unrecognised data shape');
}

/* Re-export Totals + Organizer + District + Channel + Speaker shapes for callers that want the shorthand. */
export type {
  Totals,
  Organizer,
  District,
  Channel,
  SpeakerByFormat,
  SpeakerCell,
  SpeakerFormatRow,
  Speakers,
  TopSpeaker,
  TopSpeakers,
};

/* ------------------------------------------------------------------ */
/* Trends bundle fetcher                                              */
/* ------------------------------------------------------------------ */

/**
 * Fetch the three Trends slices (`trends`, `campaign_trend`,
 * `week_labels`) plus the per-district trend classifier in a single
 * `/v1/contract` round-trip, then pluck them into the view-shaped
 * `TrendsBundle`. This is cheaper than three separate fetches and
 * guarantees the slices share the same envelope (and therefore the
 * same generated_at / anomalies).
 */
export async function fetchTrendsBundle(
  filters?: ModuleFilters,
  opts?: FetchModuleOptions,
): Promise<TrendsBundle> {
  const base = apiBase();
  const scope = filters?.scope ?? 'national';
  const url = `${base}/v1/contract?scope=${encodeURIComponent(scope)}`;

  const res = await fetch(url, authedInit(opts));
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `GET ${url} → ${res.status} ${res.statusText}${text ? ` — ${text}` : ''}`,
    );
  }

  const raw = (await res.json()) as unknown;
  const loose = zLooseEnvelope.parse(raw);

  if (!loose.data || typeof loose.data !== 'object') {
    throw new Error('fetchTrendsBundle: contract data is not an object');
  }
  const data = loose.data as Record<string, unknown>;

  const trends = zTrendsMap.parse(data.trends ?? {});
  const campaign_trend = zCampaignTrend.parse(data.campaign_trend ?? []);
  const week_labels = zWeekLabels.parse(data.week_labels ?? []);
  const districts = Array.isArray(data.districts)
    ? z.array(zTrendDistrict).parse(data.districts)
    : [];

  return {
    generated_at: loose.generated_at,
    source_pulled_at: loose.source_pulled_at,
    scope: loose.scope,
    recorded: loose.recorded,
    meta: loose.meta,
    trends,
    campaign_trend,
    week_labels,
    districts,
  };
}
